import { DeesElement, customElement, css, cssManager, html, state } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { demoApp } from '../../../demos/templates.js';
import { WebContainerEnvironment } from '../../00group-runtime/index.js';
import type { DeesWorkspace } from './dees-workspace.js';
import type { FileSystemTree } from '@tempfix/webcontainer__api';
import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';

// Every script uses Node's standard library. Opening the demo never installs packages.
const project: FileSystemTree = {
  'package.json': { file: { contents: JSON.stringify({ name: 'studio-notes', private: true, type: 'module', scripts: { test: 'node --test test/notes.test.js', preview: 'node src/preview.js' } }, null, 2) } },
  'readme.md': { file: { contents: '# Studio Notes\n\nA small project for focused work.\n\n- Open src/notes.js and make an edit.\n- Save with ⌘S or Ctrl+S.\n- Choose Scripts → test to run the real test suite.\n- Open a second terminal or inspect Problems.\n\nEverything stays in this browser runtime until the page is reloaded.\n' } },
  src: { directory: {
    'notes.js': { file: { contents: `/** A focused list of notes for today's work. */
export const notes = [
  { title: 'Sketch the first idea', done: true },
  { title: 'Make room for the details', done: false },
  { title: 'Share something worth keeping', done: false },
];

/** @param {{ title: string, done: boolean }[]} items */
export function summarize(items) {
  const complete = items.filter(item => item.done).length;
  return { complete, total: items.length };
}

export const progress = summarize(notes);
` } },
    'preview.js': { file: { contents: `import { notes, progress } from './notes.js';
console.log('Studio Notes');
console.log('────────────');
for (const note of notes) console.log((note.done ? '✓' : '○') + ' ' + note.title);
console.log('\u005cn' + progress.complete + ' of ' + progress.total + ' complete');
` } },
  } },
  test: { directory: { 'notes.test.js': { file: { contents: `import { test } from 'node:test';
import assert from 'node:assert/strict';
import { summarize } from '../src/notes.js';

test('counts completed notes', () => {
  assert.deepEqual(summarize([{ title: 'Sketch', done: true }, { title: 'Build', done: false }]), { complete: 1, total: 2 });
});

test('handles an empty notebook', () => {
  assert.deepEqual(summarize([]), { complete: 0, total: 0 });
});
` } } } },
};

@customElement('dees-demo-code-workspace')
class DeesDemoCodeWorkspace extends DeesElement {
  private environment = new WebContainerEnvironment();
  private initialization: Promise<void> | null = null;
  @state() private accessor layout = 'edit';

  static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 520px; gap: 12px; font: 13px var(--dees-font-family); }
    .demo-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; }
    .demo-layout { display: flex; align-items: center; gap: 10px; }
    .hint { color: var(--dees-color-text-muted); font-size: 12px; }
    .frame { position: relative; flex: 1; min-height: 480px; overflow: hidden; border: 1px solid var(--dees-color-border-default); border-radius: var(--dees-radius-xl); }
  `];

  async connectedCallback() {
    const connected = super.connectedCallback();
    if (!this.initialization) {
      this.initialization = this.prepareProject();
      // The workspace presents rejected initialization to the user.
      this.initialization.catch(() => {});
    }
    await connected;
  }

  private async prepareProject() {
    await this.environment.init();
    if (!this.isConnected) return;
    await this.environment.mount(project);
  }

  private setLayout(value: string) {
    this.layout = value;
    const workspace = this.shadowRoot?.querySelector<DeesWorkspace>('dees-workspace');
    if (!workspace) return;
    workspace.isFileTreeCollapsed = value === 'focus' || workspace.clientWidth <= 680;
    workspace.isTerminalCollapsed = value === 'focus';
    workspace.setTerminalHeight(value === 'run' ? 320 : 200);
  }

  render() {
    return html`<div class="demo-toolbar">
      <div class="demo-layout"><span>Workspace</span><dees-input-multitoggle label="Workspace layout" labelPosition="none" size="sm"
        .options=${[{ key: 'edit', label: 'Edit' }, { key: 'run', label: 'Run' }, { key: 'focus', label: 'Focus' }]}
        .selectedOption=${this.layout} @option-activate=${(event: CustomEvent<{ value: string }>) => this.setLayout(event.detail.value)}></dees-input-multitoggle></div>
      <span class="hint">Edit a file. Save it. Run Scripts → test.</span>
    </div><div class="frame"><dees-workspace workspaceName="Studio Notes" initialFilePath="/src/notes.js"
      .executionEnvironment=${this.environment} .initializationPromise=${this.initialization}></dees-workspace></div>`;
  }
}

export const demoFunc = () => demoApp(html`<dees-demo-code-workspace></dees-demo-code-workspace>`);
